// apps/api/src/qxcircle/qxcircle-comment-delete.ts
// Description: QXCircle delete own comment (+ cascade replies) — Slice 25.
// Owner: Masa Bayu
// Date: 2026-08-24

import { ForbiddenException, NotFoundException } from "./_nestjs-common";
import { syncQxCirclePostCommentCount } from "./qxcircle-comment-write";
import type {
  QxCircleComment,
  QxCircleCommentsResponse,
  QxCircleFeedPost,
} from "./qxcircle.types";

export function isManagedQxCircleComment(
  comment: QxCircleComment,
  accountId?: string | null,
): boolean {
  if (!accountId || !comment.ownerAccountId) {
    return false;
  }
  return comment.ownerAccountId === accountId;
}

export function presentQxCircleComment(
  comment: QxCircleComment,
  accountId?: string | null,
): QxCircleComment {
  return {
    ...comment,
    canManage: isManagedQxCircleComment(comment, accountId),
  };
}

export function buildQxCircleCommentsResponse(
  postId: string,
  comments: QxCircleComment[],
  accountId?: string | null,
): QxCircleCommentsResponse {
  const items = comments
    .filter((c) => c.postId === postId)
    .map((comment) => presentQxCircleComment(comment, accountId))
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  return {
    world: "qxcircle",
    slice: "comment",
    postId,
    items,
  };
}

export function deleteQxCircleCommentRecord(
  post: QxCircleFeedPost,
  commentId: string,
  comments: QxCircleComment[],
  accountId: string,
): { ok: true; id: string } {
  const comment = comments.find(
    (c) => c.id === commentId && c.postId === post.id,
  );
  if (!comment) {
    throw new NotFoundException(`Comment not found: ${commentId}`);
  }
  if (!isManagedQxCircleComment(comment, accountId)) {
    throw new ForbiddenException("Only your comments can be deleted");
  }
  for (let i = comments.length - 1; i >= 0; i -= 1) {
    const row = comments[i];
    if (!row || row.postId !== post.id) continue;
    if (row.id === commentId || row.parentId === commentId) {
      comments.splice(i, 1);
    }
  }
  syncQxCirclePostCommentCount(post, comments);
  return { ok: true, id: commentId };
}
